/**
 * The Import wizard's state machine (spec Screen 5: "Five steps, one screen each,
 * Back/Next throughout"). Pure, so every transition is testable without a component.
 *
 *   step        0..4 (Upload, Map columns, Duplicates, Consent, Confirm)
 *   job         the server's import document (`POST /api/crm/imports` answer, then the
 *               answer of each PUT)
 *   mapping     `{"<column index>": field}` — the column → CRM field choice; a skipped
 *               column is ABSENT (contract v1.2 CR-T-40)
 *   decisions   `{"<row_index>": "merge"|"skip"}` per duplicate row; `mode` the Merge
 *               all / Skip all choice they override
 *   consent     the two declarations
 *   stage       the stage imported clients enter at (default Active client)
 */
export const STEPS = Object.freeze(["upload", "map", "duplicates", "consent", "confirm"]);

export const initialImportState = Object.freeze({
  step: 0,
  job: null,
  mapping: {},
  mode: "merge_all",
  decisions: {},
  consent: { previously_emailed: false, has_permission: false },
  stage: "active",
  summary: null,
});

/** A job's mapping as the wizard holds it: the saved one, else the server's suggestion. */
const mappingOf = (job) => ({ ...(job?.mapping && Object.keys(job.mapping).length ? job.mapping : job?.suggested_mapping || {}) });

export function importReducer(state, action) {
  switch (action.type) {
    case "resume":
    case "uploaded":
      return {
        ...initialImportState,
        job: action.job,
        mapping: mappingOf(action.job),
        consent: { ...initialImportState.consent, ...(action.job?.consent || {}) },
        stage: action.job?.stage || initialImportState.stage,
        mode: action.job?.duplicate_mode || initialImportState.mode,
        step: action.type === "resume" ? (action.step ?? 1) : 0,
      };
    case "map": {
      const mapping = { ...state.mapping };
      const key = String(action.column);
      if (!action.field) delete mapping[key];
      else {
        // One column per CRM field: choosing `email` for a column frees any other.
        Object.keys(mapping).forEach((k) => { if (mapping[k] === action.field) delete mapping[k]; });
        mapping[key] = action.field;
      }
      return { ...state, mapping };
    }
    case "mode":
      return { ...state, mode: action.mode, decisions: {} };
    case "decide":
      return { ...state, decisions: { ...state.decisions, [String(action.row)]: action.decision } };
    case "consent":
      return { ...state, consent: { ...state.consent, [action.key]: action.value } };
    case "stage":
      return { ...state, stage: action.stage };
    case "job":
      return { ...state, job: action.job };
    case "summary":
      return { ...state, summary: action.summary };
    case "goto":
      return { ...state, step: Math.max(0, Math.min(STEPS.length - 1, action.step)) };
    default:
      return state;
  }
}

/** Next is enabled when the current step's own condition holds (spec: email must be mapped). */
export const canAdvance = (state) => {
  if (state.step === 0) return !!state.job;
  if (state.step === 1) return Object.values(state.mapping).includes("email");
  return true;
};
